/**
 * StandalonePreview — Public full-screen mobile menu preview.
 * Route: /public-preview/:menuId
 * Renders a clean mobile phone frame with the menu on a minimal background.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '../../../ui/utils';
import {
  Package, Plus, Minus, X, ShoppingCart, FolderOpen,
  Monitor, Smartphone, Link2Off, Clock, FileX, Share2, Search, Check, Timer,
} from 'lucide-react';
import type { MenuProfile, MenuItem, MenuItemOption, MenuCategory, PricingStrategy, AvailabilitySchedule, TimeSlot } from '../types';
import { isUnassignedCat } from '../utils';
import { SECONDARY_LANGUAGES } from '../types';
import { isPromoExpired, discountLabel, fundingLabel } from '../services/PromoService';
import { shouldHidePrice, formatCardPrice } from '../utils/priceFormatter';
import { genId } from '../utils';
import { useScrollSpy } from '../hooks/useScrollSpy';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

/** Resolve image URL for public (unauthenticated) preview — uses /api/public/media/ */
function publicMediaUrl(url?: string, menuId?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/api/public-media/')) return `${API_BASE}${url}`;
  if (url.startsWith('/api/public/media/')) {
    if (!menuId || url.includes('menuId=')) return `${API_BASE}${url}`;
    return `${API_BASE}${url}${url.includes('?') ? '&' : '?'}menuId=${encodeURIComponent(menuId)}`;
  }
  // Strip /api/media/ prefix to get the raw R2 key, then serve via public endpoint
  const key = url.startsWith('/api/media/') ? url.slice('/api/media/'.length) : url.replace(/^\//, '');
  const qs = menuId ? `?menuId=${encodeURIComponent(menuId)}` : '';
  return `${API_BASE}/api/public/media/${key}${qs}`;
}

interface PublicMenuData {
  id: string;
  name: string;
  brandName: string;
  brandLogoUrl?: string;
  logoUrl?: string;
  brandLogo?: string;
  tag: string;
  type: string;
  categories: (MenuCategory & { _available?: boolean; items: (MenuItem & { _available?: boolean })[] })[];
  currency: string;
  secondaryLanguage: string;
  profileAvailable?: boolean;
  emergencyShutdown?: boolean;
  pricingStrategy?: PricingStrategy;
  availabilitySchedule?: AvailabilitySchedule | null;
}

interface CartLine {
  id: string;
  item: MenuItem;
  opts: { grp: string; opt: MenuItemOption }[];
  qty: number;
  specialRequest?: string;
}

type UnavailableReason = 'not-found' | 'disabled' | 'expired' | 'unpublished';

function formatScheduleLabel(schedule?: AvailabilitySchedule | null): string | null {
  if (!schedule || schedule.alwaysOn) return null;
  const days = schedule.days?.length === 7 ? 'Every day' : (schedule.days || []).join(', ');
  if (!schedule.slots || schedule.slots.length === 0) return days;
  const slots = schedule.slots.map((s: TimeSlot) => `${s.start}–${s.end}`).join(', ');
  return `${days} · ${slots}`;
}

export default function StandalonePreview() {
  const { menuId } = useParams<{ menuId: string }>();

  /** Image URL helper — binds current menuId for auth validation */
  const pubImg = (url?: string) => publicMediaUrl(url, menuId);

  const [menuData, setMenuData] = useState<PublicMenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState<UnavailableReason | null>(null);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [lang, setLang] = useState<'EN' | 'AR'>('EN');
  const [activeCat, setActiveCat] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [drawer, setDrawer] = useState<MenuItem | null>(null);
  const [detailItem, setDetailItem] = useState<MenuItem | null>(null);
  const [drawerSelections, setDrawerSelections] = useState<Record<string, Set<string>>>({});
  const [drawerQty, setDrawerQty] = useState<Record<string, Record<string, number>>>({});
  const [drawerSpecialReq, setDrawerSpecialReq] = useState('');
  const [drawerItemQty, setDrawerItemQty] = useState(1);
  const [detailSpecialReq, setDetailSpecialReq] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [mode, setMode] = useState<'mobile' | 'desktop'>('desktop');
  const [shareCopied, setShareCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrollingTo, setIsScrollingTo] = useState(false);

  // Refs for scroll-spy
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const catNavDesktopRef = useRef<HTMLDivElement>(null);
  const catNavMobileRef = useRef<HTMLDivElement>(null);
  const scrollSyncTimeoutRef = useRef<number | null>(null);

  // ScrollSpy: auto-highlight active category pill on scroll
  const scrollSpyCatIds = useMemo(() => {
    if (!menuData) return [];
    return menuData.categories.filter(c => !c.hidden && !isUnassignedCat(c.id)).map(c => c.id);
  }, [menuData]);
  const { scrollPillIntoView } = useScrollSpy({
    scrollRef: mode === 'desktop' ? desktopScrollRef : mobileScrollRef,
    navRef: mode === 'desktop' ? catNavDesktopRef : catNavMobileRef,
    categoryIds: scrollSpyCatIds,
    idPrefix: mode === 'desktop' ? 'spd-cat-' : 'sp-cat-',
    enabled: !!menuData,
    paused: isScrollingTo,
    onActiveCatChange: setActiveCat,
  });
  useEffect(() => { scrollPillIntoView(activeCat); }, [activeCat, mode]);

  const releaseScrollLock = useCallback(() => {
    if (scrollSyncTimeoutRef.current) window.clearTimeout(scrollSyncTimeoutRef.current);
    scrollSyncTimeoutRef.current = window.setTimeout(() => {
      setIsScrollingTo(false);
      scrollSyncTimeoutRef.current = null;
    }, 800);
  }, []);

  const scrollToCategory = useCallback((catId: string, sectionPrefix: 'sp-cat-' | 'spd-cat-') => {
    setActiveCat(catId);
    setIsScrollingTo(true);
    scrollPillIntoView(catId);
    document.getElementById(`${sectionPrefix}${catId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    releaseScrollLock();
  }, [releaseScrollLock, scrollPillIntoView]);

  useEffect(() => () => {
    if (scrollSyncTimeoutRef.current) window.clearTimeout(scrollSyncTimeoutRef.current);
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(''), 2500);
    return () => clearTimeout(t);
  }, [toastMessage]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/menu/${menuId}`, { cache: 'no-store' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as Record<string, unknown>;
          const err = (body.error as string) || '';
          if (err === 'Unavailable' && String(body.message || '').includes('disabled')) setUnavailable('disabled');
          else if (err === 'Expired') setUnavailable('expired');
          else if (err === 'Unavailable') setUnavailable('unpublished');
          else setUnavailable('not-found');
          setLoading(false);
          return;
        }
        const json = await res.json() as { data: PublicMenuData };
        const d = json.data;
        // OFFERS category sorting: move OFFERS to top
        if (d.categories) {
          d.categories.sort((a, b) => {
            const aOffer = (a.nameEN || '').toUpperCase() === 'OFFERS' ? -1 : 0;
            const bOffer = (b.nameEN || '').toUpperCase() === 'OFFERS' ? -1 : 0;
            return aOffer - bOffer;
          });
        }
        setMenuData(d);
        if (d.categories.length) setActiveCat(d.categories[0].id);
        document.title = `${d.name} — ${d.brandName} | Menu Preview`;
      } catch {
        setUnavailable('not-found');
      }
      setLoading(false);
    })();
  }, [menuId]);

  useEffect(() => {
    setBrandLogoFailed(false);
  }, [menuData?.brandLogoUrl, menuData?.logoUrl, menuData?.brandLogo]);

  const isAR = lang === 'AR';
  const nm = (en: string, ar: string) => (isAR && ar ? ar : en);

  const secLang = SECONDARY_LANGUAGES.find(l => l.code === (menuData?.secondaryLanguage || 'AR')) || SECONDARY_LANGUAGES[0];
  const currencySymbol = menuData?.currency || 'QAR';
  const pricingStrategy: PricingStrategy = menuData?.pricingStrategy || 'LOWEST';

  const cartCount = cart.reduce((a, c) => a + c.qty, 0);
  const cartTotal = cart.reduce((a, c) => {
    const optP = c.opts.reduce((b, o) => b + o.opt.extraPrice, 0);
    return a + (c.item.basePrice + optP) * c.qty;
  }, 0);

  // Search filter
  const filteredCategories = useMemo(() => {
    if (!menuData) return [];
    const cats = menuData.categories.filter(c => !c.hidden && !isUnassignedCat(c.id));
    if (!searchQuery.trim()) return cats;
    const q = searchQuery.trim().toLowerCase();
    return cats.map(cat => {
      const matchedItems = cat.items.filter(i =>
        !i.hidden &&
        !(i.campaignMeta && isPromoExpired(i)) &&
        (i.nameEN.toLowerCase().includes(q) || (i.nameAR || '').toLowerCase().includes(q) ||
         (i.descriptionEN || '').toLowerCase().includes(q) || (i.descriptionAR || '').toLowerCase().includes(q))
      );
      if (matchedItems.length > 0) return { ...cat, items: matchedItems };
      if ((cat.nameEN || '').toLowerCase().includes(q) || (cat.nameAR || '').toLowerCase().includes(q)) return cat;
      return null;
    }).filter(Boolean) as typeof cats;
  }, [menuData, searchQuery]);

  const openDrawer = (item: MenuItem) => {
    const groups = item.optionGroups || [];
    if (groups.length > 0) {
      const initial: Record<string, Set<string>> = {};
      const initQty: Record<string, Record<string, number>> = {};
      groups.forEach(g => { initial[g.id] = new Set(); initQty[g.id] = {}; g.options.forEach(o => { initQty[g.id][o.id] = 0; }); });
      setDrawerSelections(initial);
      setDrawerQty(initQty);
      setDrawerSpecialReq('');
      setDrawerItemQty(1);
      setDrawer(item);
    } else {
      setDetailSpecialReq('');
      setDetailItem(item);
    }
  };

  const toggleOpt = (grpId: string, optId: string, max: number) => {
    setDrawerSelections(prev => {
      const s = new Set(prev[grpId] || []);
      if (s.has(optId)) { s.delete(optId); }
      else if (max === 1) { s.clear(); s.add(optId); }
      else { if (max > 0 && s.size >= max) return prev; s.add(optId); }
      return { ...prev, [grpId]: s };
    });
  };

  /** Adjust option quantity for allowMultipleQty groups */
  const changeOptQty = (grpId: string, optId: string, delta: number, max: number) => {
    setDrawerQty(prev => {
      const grpQ = { ...(prev[grpId] || {}) };
      const cur = grpQ[optId] || 0;
      const next = Math.max(0, cur + delta);
      // Guard: total across all options in group must not exceed maxSelection
      if (delta > 0 && max > 0) {
        const total = Object.values(grpQ).reduce((a, v) => a + v, 0);
        if (total >= max) return prev;
      }
      grpQ[optId] = next;
      return { ...prev, [grpId]: grpQ };
    });
  };

  const addToCart = () => {
    if (!drawer || !drawerValid) return;
    const opts: { grp: string; opt: MenuItemOption }[] = [];
    (drawer.optionGroups || []).forEach(g => {
      if (g.allowMultipleQty) {
        // Quantity-based: add one entry per quantity unit
        const qMap = drawerQty[g.id] || {};
        g.options.forEach(o => { const q = qMap[o.id] || 0; for (let i = 0; i < q; i++) opts.push({ grp: g.nameEN, opt: o }); });
      } else {
        const sel = drawerSelections[g.id] || new Set();
        g.options.forEach(o => { if (sel.has(o.id)) opts.push({ grp: g.nameEN, opt: o }); });
      }
    });
    setCart(prev => [...prev, { id: genId(), item: drawer, opts, qty: drawerItemQty, specialRequest: drawerSpecialReq.trim() || undefined }]);
    setToastMessage(`${drawer.nameEN} added to cart`);
    setDrawer(null);
  };

  const addFromDetail = () => {
    if (!detailItem) return;
    setCart(prev => [...prev, { id: genId(), item: detailItem, opts: [], qty: 1, specialRequest: detailSpecialReq.trim() || undefined }]);
    setToastMessage(`${detailItem.nameEN} added to cart`);
    setDetailItem(null);
    setDetailSpecialReq('');
  };

  const updateQty = (id: string, delta: number) => {
    setCart(prev => prev.map(c => c.id === id ? { ...c, qty: Math.max(0, c.qty + delta) } : c).filter(c => c.qty > 0));
  };

  // Validate required option groups
  const drawerValid = useMemo(() => {
    if (!drawer) return false;
    return (drawer.optionGroups || []).filter(g => !g.hidden).every(g => {
      const min = g.minSelection ?? 0;
      if (min <= 0) return true;
      if (g.allowMultipleQty) {
        const qMap = drawerQty[g.id] || {};
        const total = Object.values(qMap).reduce((a, v) => a + v, 0);
        return total >= min;
      }
      const sel = drawerSelections[g.id];
      return sel && sel.size >= min;
    });
  }, [drawer, drawerSelections, drawerQty]);

  const drawerExtraPrice = useMemo(() => {
    if (!drawer) return 0;
    return (drawer.optionGroups || []).reduce((a, grp) => {
      if (grp.allowMultipleQty) {
        const qMap = drawerQty[grp.id] || {};
        return a + grp.options.reduce((b, o) => b + o.extraPrice * (qMap[o.id] || 0), 0);
      }
      const sel = drawerSelections[grp.id] as Set<string> | undefined;
      if (!sel) return a;
      return a + grp.options.filter(o => sel.has(o.id)).reduce((b, o) => b + o.extraPrice, 0);
    }, 0);
  }, [drawer, drawerSelections, drawerQty]);

  // Promo-aware price renderer for detail views
  const renderDetailPrice = (it: MenuItem) => {
    if (it.campaignMeta && !isPromoExpired(it)) {
      return (
        <span className="mt-3 block">
          <span className="text-lg text-slate-400 line-through">{currencySymbol} {it.campaignMeta.originalPrice.toFixed(2)}</span>{' '}
          <span className="text-2xl font-bold text-emerald-600">{currencySymbol} {it.campaignMeta.promoPrice.toFixed(2)}</span>
          <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">
            {discountLabel(it, currencySymbol)} — {fundingLabel(it)}
          </span>
        </span>
      );
    }
    if (shouldHidePrice(it)) {
      const fp = formatCardPrice(it, currencySymbol, pricingStrategy);
      return fp.text ? <span className="text-2xl font-bold text-indigo-600 mt-3 block">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</span> : fp.badge ? <span className="text-sm text-slate-400 mt-3 block">{fp.badge}</span> : null;
    }
    return <span className="text-2xl font-bold text-indigo-600 mt-3 block">{currencySymbol} {it.basePrice.toFixed(2)}</span>;
  };
  const detailCartPrice = (it: MenuItem) => {
    if (it.campaignMeta && !isPromoExpired(it)) return it.campaignMeta.promoPrice.toFixed(2);
    if (shouldHidePrice(it)) return null;
    return it.basePrice.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-50 flex items-center justify-center">
        <div className="animate-pulse text-indigo-400 text-sm font-bold">Loading menu…</div>
      </div>
    );
  }

  if (unavailable) {
    const msgs: Record<UnavailableReason, { icon: React.ReactNode; title: string; desc: string }> = {
      'not-found': { icon: <FileX size={48} className="mx-auto text-slate-300 mb-4" />, title: 'Menu Not Found', desc: 'This menu link may be invalid or the menu was removed.' },
      'disabled': { icon: <Link2Off size={48} className="mx-auto text-amber-300 mb-4" />, title: 'Link Disabled', desc: 'The owner has disabled public access to this menu.' },
      'expired': { icon: <Clock size={48} className="mx-auto text-red-300 mb-4" />, title: 'Link Expired', desc: 'This menu link has expired. Contact the owner for a new link.' },
      'unpublished': { icon: <FileX size={48} className="mx-auto text-indigo-300 mb-4" />, title: 'Menu Not Published', desc: 'This menu has not been published yet.' },
    };
    const m = msgs[unavailable];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm">
          {m.icon}
          <h2 className="text-lg font-bold text-slate-800 mb-2">{m.title}</h2>
          <p className="text-sm text-slate-500 mb-4">{m.desc}</p>
          <p className="text-[10px] text-slate-400">Powered by ClickIt Engine</p>
        </div>
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm">
          <Package size={48} className="mx-auto text-indigo-200 mb-4" />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Menu Not Found</h2>
          <p className="text-sm text-slate-500 mb-4">This menu link may have expired or the menu was removed.</p>
          <p className="text-[10px] text-slate-400">Powered by ClickIt Engine</p>
        </div>
      </div>
    );
  }

  const profile = menuData;
  const menuClosed = profile.profileAvailable === false || profile.emergencyShutdown === true;
  const headerLogoUrl = pubImg(profile.brandLogoUrl || profile.logoUrl || profile.brandLogo);
  const brandInitial = (profile.brandName || profile.name || 'M').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex flex-col">
      {/* ═══ Top Navigation Bar ═══ */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[4.5rem] gap-4">
            {/* Brand logo + name */}
            <div className="flex items-center gap-3 shrink-0">
              {headerLogoUrl && !brandLogoFailed ? (
                <img src={headerLogoUrl} alt={profile.brandName} className="w-12 h-12 rounded-xl object-cover shadow-sm" onError={() => setBrandLogoFailed(true)} />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-indigo-500 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-sm">
                  {brandInitial}
                </div>
              )}
              <div className="hidden sm:block">
                <h1 className="text-base font-bold text-slate-900 leading-tight">{profile.brandName}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-[10px] text-slate-400 leading-tight">{profile.name}</p>
                  {(() => {
                    const sLabel = formatScheduleLabel(profile.availabilitySchedule);
                    return sLabel ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                        <Timer size={10} className="shrink-0" /> {sLabel}
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>

            {/* Search bar */}
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search menu items..."
                  className="w-full pl-9 pr-8 py-2 bg-slate-100/80 border border-slate-200/60 rounded-xl text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 focus:bg-white transition-all"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 border border-slate-200/60">
                <button onClick={() => setMode('desktop')} className={cn('p-1.5 rounded-md transition-all', mode === 'desktop' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')} title="Web View"><Monitor size={14} /></button>
                <button onClick={() => setMode('mobile')} className={cn('p-1.5 rounded-md transition-all', mode === 'mobile' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')} title="Mobile View"><Smartphone size={14} /></button>
              </div>
              <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 border border-slate-200/60">
                <button onClick={() => setLang('EN')} className={cn('px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all', lang === 'EN' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')}>EN</button>
                <button onClick={() => setLang('AR')} className={cn('px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all', lang === 'AR' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')}>{secLang.code.toUpperCase()}</button>
              </div>
              {cart.length > 0 && <button onClick={() => setCart([])} className="text-[10px] text-red-500 font-bold hover:underline px-2">Clear</button>}
            </div>
          </div>
        </div>
      </div>

      {mode === 'mobile' ? (
      /* ═══════ MOBILE VIEW ═══════ */
      <div className="w-[390px] mx-auto mt-6 bg-white border-2 border-slate-300 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative" style={{ height: '780px' }}>
        {/* Notch */}
        <div className="bg-slate-900 h-7 flex items-center justify-center shrink-0"><div className="w-20 h-4 bg-slate-800 rounded-full" /></div>

        {/* CLOSED overlay */}
        {menuClosed && (
          <div className="absolute inset-0 z-30 bg-black/60 rounded-[2.5rem] flex flex-col items-center justify-center text-white">
            <Clock size={48} className="mb-3 opacity-80" />
            <p className="text-lg font-bold">Menu is currently CLOSED</p>
            <p className="text-xs text-white/60 mt-1">{profile.emergencyShutdown ? 'Temporarily shut down by management' : 'Outside operating hours'}</p>
          </div>
        )}

        {/* Header */}
        <div className={cn('bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-5 py-4 shrink-0', isAR && 'text-right')} dir={isAR ? 'rtl' : 'ltr'}>
          <div className="flex items-center gap-3">
            {headerLogoUrl && !brandLogoFailed ? (
              <img src={headerLogoUrl} alt={profile.brandName} className="w-12 h-12 rounded-xl object-cover" onError={() => setBrandLogoFailed(true)} />
            ) : (
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-lg font-bold">{brandInitial}</div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold">{profile.brandName}</h2>
              <p className="text-indigo-200 text-xs">{profile.name}</p>
              {(() => {
                const sLabel = formatScheduleLabel(profile.availabilitySchedule);
                return sLabel ? (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Timer size={10} className="text-indigo-200 shrink-0" />
                    <span className="text-[10px] text-indigo-200 font-medium">{sLabel}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        </div>

        {/* Category pills */}
        <div ref={catNavMobileRef} className="bg-white border-b border-slate-100 px-3 overflow-x-auto shrink-0">
          <div className="flex items-center gap-1 py-2">
            {filteredCategories.map(cat => (
              <button key={cat.id} data-pill={cat.id} onClick={() => scrollToCategory(cat.id, 'sp-cat-')}
                className={cn('px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all',
                  activeCat === cat.id ? 'bg-indigo-600 text-white' : 'text-slate-500 bg-slate-50')}>
                {nm(cat.nameEN, cat.nameAR)}
              </button>
            ))}
          </div>
        </div>

        {/* Menu content */}
        <div ref={mobileScrollRef} className={cn('flex-1 overflow-y-auto bg-gray-50', isAR && 'text-right')} dir={isAR ? 'rtl' : 'ltr'} style={{ paddingBottom: cartCount > 0 ? '70px' : '16px' }}>
          {filteredCategories.map(cat => (
            <div key={cat.id} id={`sp-cat-${cat.id}`} data-section-id={cat.id} className={cn('py-3', cat._available === false && 'opacity-40')}>
              <h3 className="px-4 text-[11px] font-bold text-indigo-700 uppercase tracking-wider mb-2">{nm(cat.nameEN, cat.nameAR)}</h3>
              {cat.items.filter(i => !i.hidden && !(i.campaignMeta && isPromoExpired(i))).map(item => (
                <div key={item.id}
                  className={cn('bg-white mx-3 mb-2 rounded-2xl shadow-sm border border-slate-100 overflow-hidden cursor-pointer hover:shadow-md transition-all active:scale-[0.99]', (item as any)._available === false && 'opacity-40')}
                  onClick={() => openDrawer(item)}>
                  <div className={cn('flex', isAR && 'flex-row-reverse')}>
                    <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                      <div>
                        <p className="text-[13px] font-bold text-slate-900 leading-tight">{nm(item.nameEN, item.nameAR)}</p>
                        {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {item.campaignMeta && !isPromoExpired(item) ? (
                          <>
                            <span className="text-[10px] text-slate-400 line-through">{currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                            <span className="text-xs font-bold text-emerald-600">{currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                            <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">{discountLabel(item, currencySymbol)} — {fundingLabel(item)}</span>
                          </>
                        ) : (() => {
                          const fp = formatCardPrice(item, currencySymbol, pricingStrategy);
                          return fp.text
                            ? <>{fp.prefix && <span className="text-[9px] font-normal text-slate-400 mr-1">{fp.prefix}</span>}<span className="text-xs font-bold text-indigo-600">{fp.text}</span></>
                            : fp.badge ? <span className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">{fp.badge}</span> : null;
                        })()}
                        {!shouldHidePrice(item) && (item.optionGroups || []).length > 0 && <span className="text-[9px] text-slate-400">Customizable</span>}
                      </div>
                    </div>
                    <div className="w-24 h-24 shrink-0 relative">
                      {item.image ? <img loading="lazy" src={pubImg(item.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center"><Package size={20} className="text-slate-200" /></div>}
                      <button className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg"><Plus size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Floating cart bar */}
        {cartCount > 0 && !showCart && !drawer && !detailItem && (
          <div className="absolute bottom-6 left-3 right-3 z-10">
            <button onClick={() => setShowCart(true)}
              className="w-full flex items-center justify-between px-4 py-3 bg-indigo-600 text-white rounded-2xl shadow-xl hover:bg-indigo-700">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-indigo-500 rounded-lg flex items-center justify-center text-[10px] font-bold">{cartCount}</div>
                <span className="text-xs font-bold">View Cart</span>
              </div>
              <span className="text-xs font-bold">{currencySymbol} {cartTotal.toFixed(2)}</span>
            </button>
          </div>
        )}

        {/* Modifier drawer */}
        {drawer && (
          <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setDrawer(null)}>
            <div className="bg-white rounded-t-2xl max-h-[75%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Modifier header */}
              {drawer.image ? (
                <div className="w-full h-44 relative shrink-0">
                  <img loading="lazy" src={pubImg(drawer.image)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <button onClick={() => setDrawer(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center"><X size={16} /></button>
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h3 className="text-lg font-bold">{nm(drawer.nameEN, drawer.nameAR)}</h3>
                    {drawer.campaignMeta && !isPromoExpired(drawer) ? (
                      <p className="text-sm font-bold mt-0.5"><span className="line-through opacity-70">{currencySymbol} {drawer.campaignMeta.originalPrice.toFixed(2)}</span> <span className="text-emerald-300">{currencySymbol} {drawer.campaignMeta.promoPrice.toFixed(2)}</span></p>
                    ) : !shouldHidePrice(drawer) ? (
                      <p className="text-sm font-bold mt-0.5">{currencySymbol} {drawer.basePrice.toFixed(2)}</p>
                    ) : (
                      (() => { const fp = formatCardPrice(drawer, currencySymbol, pricingStrategy); return fp.text ? <p className="text-sm font-bold mt-0.5">{fp.prefix && <span className="opacity-70 mr-1">{fp.prefix}</span>}{fp.text}</p> : fp.badge ? <p className="text-sm opacity-70 mt-0.5">{fp.badge}</p> : null; })()
                    )}
                  </div>
                </div>
              ) : (
                <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between shrink-0">
                  <div>
                    <h3 className="text-base font-bold">{nm(drawer.nameEN, drawer.nameAR)}</h3>
                    {drawer.campaignMeta && !isPromoExpired(drawer) ? (
                      <p className="text-sm font-bold"><span className="text-slate-400 line-through">{currencySymbol} {drawer.campaignMeta.originalPrice.toFixed(2)}</span> <span className="text-emerald-600">{currencySymbol} {drawer.campaignMeta.promoPrice.toFixed(2)}</span></p>
                    ) : !shouldHidePrice(drawer) ? (
                      <p className="text-sm text-indigo-600 font-bold">{currencySymbol} {drawer.basePrice.toFixed(2)}</p>
                    ) : (
                      (() => { const fp = formatCardPrice(drawer, currencySymbol, pricingStrategy); return fp.text ? <p className="text-sm text-indigo-600 font-bold">{fp.prefix && <span className="font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</p> : fp.badge ? <p className="text-[11px] text-slate-400">{fp.badge}</p> : null; })()
                    )}
                  </div>
                  <button onClick={() => setDrawer(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
                </div>
              )}
              {/* Option groups */}
              <div className="flex-1 overflow-y-auto px-5 py-3 space-y-5">
                {(drawer.optionGroups || []).filter(g => !g.hidden).map(grp => {
                  const sel = drawerSelections[grp.id] || new Set();
                  const qMap = drawerQty[grp.id] || {};
                  const min = grp.minSelection ?? 0;
                  const max = grp.maxSelection ?? 0;
                  const isQtyMode = grp.allowMultipleQty;
                  const qtyTotal = isQtyMode ? Object.values(qMap).reduce((a, v) => a + v, 0) : sel.size;
                  const selCount = isQtyMode ? qtyTotal : sel.size;
                  const metMin = selCount >= min;
                  const isReq = min > 0;
                  return (
                    <div key={grp.id}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-bold text-slate-800">{nm(grp.nameEN, grp.nameAR)}</p>
                          <p className="text-[11px] text-slate-400">{min > 0 && `Min ${min}`}{min > 0 && max > 0 && ' · '}{max > 0 && `Max ${max}`}{!min && !max && 'Optional'}</p>
                        </div>
                        {isReq && !metMin && <div className="w-5 h-5 rounded-full border-2 border-red-300 flex items-center justify-center"><span className="text-[9px] text-red-400">!</span></div>}
                        {metMin && isReq && <div className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center"><Check size={11} className="text-white" /></div>}
                      </div>
                      <div className="space-y-1.5">
                        {grp.options.filter(o => !o.hidden).map(opt => {
                          if (isQtyMode) {
                            const qty = qMap[opt.id] || 0;
                            const totalQ = Object.values(qMap).reduce((a, v) => a + v, 0);
                            const atMax = max > 0 && totalQ >= max;
                            return (
                              <div key={opt.id} className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all',
                                qty > 0 ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200')}>
                                <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                                <div className="flex items-center gap-2">
                                  {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{currencySymbol} {opt.extraPrice.toFixed(2)}{qty > 1 ? ` × ${qty}` : ''}</span>}
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => changeOptQty(grp.id, opt.id, -1, max)} disabled={qty === 0}
                                      className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                        qty === 0 ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Minus size={12} /></button>
                                    <span className="w-5 text-center text-xs font-bold text-slate-700">{qty}</span>
                                    <button onClick={() => changeOptQty(grp.id, opt.id, 1, max)} disabled={atMax}
                                      className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                        atMax ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Plus size={12} /></button>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          const selected = sel.has(opt.id);
                          const disabled = !selected && max > 0 && selCount >= max && max !== 1;
                          return (
                            <button key={opt.id} onClick={() => !disabled && toggleOpt(grp.id, opt.id, max)} disabled={disabled}
                              className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                                selected ? 'border-indigo-400 bg-indigo-50' : disabled ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 hover:border-indigo-300')}>
                              <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                              <div className="flex items-center gap-2">
                                {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{currencySymbol} {opt.extraPrice.toFixed(2)}</span>}
                                <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', selected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300')}>{selected && <Check size={12} className="text-white" />}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Special Request */}
              {drawer.allowSpecialRequests !== false && (
                <div className="px-5 pb-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-xs font-bold text-slate-700">Special Request</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                  </div>
                  <textarea
                    value={drawerSpecialReq}
                    onChange={e => setDrawerSpecialReq(e.target.value.slice(0, 200))}
                    placeholder="Leave any instructions to the restaurant"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                    rows={2}
                  />
                  <p className="text-[9px] text-slate-300 text-right mt-0.5">{drawerSpecialReq.length}/200</p>
                </div>
              )}
              {/* Add to cart footer — Pizza Hut style */}
              <div className="px-4 pb-4 pt-3 border-t border-slate-100 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 border border-slate-200 rounded-xl px-1.5 py-1">
                    <button onClick={() => setDrawerItemQty(q => Math.max(1, q - 1))} disabled={drawerItemQty <= 1}
                      className={cn('w-7 h-7 rounded-lg flex items-center justify-center transition-colors', drawerItemQty <= 1 ? 'text-slate-300' : 'text-slate-600 hover:bg-slate-100')}><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm font-bold text-slate-800">{drawerItemQty}</span>
                    <button onClick={() => setDrawerItemQty(q => q + 1)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"><Plus size={14} /></button>
                  </div>
                  <button onClick={addToCart} disabled={!drawerValid}
                    className={cn('flex-1 py-2.5 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-1.5',
                      drawerValid ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' : 'bg-slate-200 text-slate-400 cursor-not-allowed')}>
                    {drawerValid ? (
                      <>
                        <span>{shouldHidePrice(drawer) ? `${currencySymbol} ${(drawerExtraPrice * drawerItemQty).toFixed(2)}` : `${currencySymbol} ${((drawer.basePrice + drawerExtraPrice) * drawerItemQty).toFixed(2)}`}</span>
                        <span>Add to Cart</span>
                      </>
                    ) : 'Select required options'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Item detail drawer */}
        {detailItem && !drawer && (
          <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setDetailItem(null)}>
            <div className="bg-white rounded-t-2xl max-h-[80%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              {detailItem.image ? (
                <div className="w-full h-56 relative bg-gradient-to-br from-slate-100 to-slate-50">
                  <img loading="lazy" src={pubImg(detailItem.image)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center"><X size={16} /></button>
                </div>
              ) : (
                <div className="w-full h-40 relative bg-gradient-to-br from-indigo-50 to-slate-100 flex items-center justify-center">
                  <Package size={48} className="text-indigo-200" />
                  <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/20 text-white flex items-center justify-center"><X size={16} /></button>
                </div>
              )}
              <div className="px-5 pt-4 pb-3">
                <h3 className="text-xl font-bold text-slate-900">{nm(detailItem.nameEN, detailItem.nameAR)}</h3>
                {(isAR ? detailItem.descriptionAR : detailItem.descriptionEN) && (
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed whitespace-pre-wrap">{nm(detailItem.descriptionEN || '', detailItem.descriptionAR || '')}</p>
                )}
                {renderDetailPrice(detailItem)}
              </div>
              {/* Special Request — decoupled from option groups */}
              {detailItem.allowSpecialRequests !== false && (
                <div className="px-5 pb-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-xs font-bold text-slate-700">Special Request</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                  </div>
                  <textarea
                    value={detailSpecialReq}
                    onChange={e => setDetailSpecialReq(e.target.value.slice(0, 200))}
                    placeholder="Leave any instructions to the restaurant"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                    rows={2}
                  />
                  <p className="text-[9px] text-slate-300 text-right mt-0.5">{detailSpecialReq.length}/200</p>
                </div>
              )}
              <div className="px-5 pb-5 pt-2 border-t border-slate-100 mt-auto">
                <button onClick={addFromDetail} className="w-full py-3.5 rounded-2xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg">
                  {detailCartPrice(detailItem) ? `Add to Cart — ${currencySymbol} ${detailCartPrice(detailItem)}` : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cart slideout */}
        {showCart && (
          <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40 rounded-[2.5rem] overflow-hidden" onClick={() => setShowCart(false)}>
            <div className="bg-white rounded-t-2xl max-h-[75%] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
                <h4 className="text-sm font-bold flex items-center gap-2"><ShoppingCart size={14} className="text-indigo-600" /> Your Cart</h4>
                <button onClick={() => setShowCart(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {cart.map(c => {
                  const optP = c.opts.reduce((a, o) => a + o.opt.extraPrice, 0);
                  const lineT = (c.item.basePrice + optP) * c.qty;
                  return (
                    <div key={c.id} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold truncate">{nm(c.item.nameEN, c.item.nameAR)}</p>
                        {c.opts.length > 0 && <p className="text-[9px] text-slate-400 truncate">{c.opts.map(o => nm(o.opt.nameEN, o.opt.nameAR)).join(', ')}</p>}
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => updateQty(c.id, -1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Minus size={10} /></button>
                        <span className="text-xs font-bold w-5 text-center">{c.qty}</span>
                        <button onClick={() => updateQty(c.id, 1)} className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Plus size={10} /></button>
                      </div>
                      <span className="text-xs font-bold text-indigo-600 whitespace-nowrap">{currencySymbol} {lineT.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-2 shrink-0">
                <div className="flex items-center justify-between"><span className="text-sm font-bold">Total</span><span className="text-base font-bold text-indigo-600">{currencySymbol} {cartTotal.toFixed(2)}</span></div>
                <button className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 shadow-lg">Checkout (Demo)</button>
              </div>
            </div>
          </div>
        )}

        {/* Home bar */}
        <div className="bg-slate-100 h-5 flex items-center justify-center shrink-0"><div className="w-28 h-1 bg-slate-300 rounded-full" /></div>
      </div>
      ) : (
      /* ═══════ DESKTOP / WEB VIEW ═══════ */
      <div className="w-full flex-1 flex flex-col" dir={isAR ? 'rtl' : 'ltr'}>

          {/* Sticky category nav */}
          <div className="sticky top-16 z-20 bg-white border-b border-slate-200">
            <div ref={catNavDesktopRef} className="max-w-[1600px] mx-auto px-6 overflow-x-auto">
              <div className="flex items-center gap-1.5 py-2.5">
                {filteredCategories.map(cat => (
                  <button key={cat.id} data-pill={cat.id} onClick={() => scrollToCategory(cat.id, 'spd-cat-')}
                    className={cn('px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all',
                      activeCat === cat.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 bg-slate-50 hover:bg-slate-100 hover:text-indigo-600')}>
                    {nm(cat.nameEN, cat.nameAR)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Body: Menu + Cart sidebar */}
          <div className="flex flex-1 min-h-0">
            <div ref={desktopScrollRef} className={cn('flex-1 overflow-y-auto', isAR && 'text-right')}>
              <div className="max-w-[1600px] mx-auto p-6">
              {menuClosed && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-bold flex items-center gap-2">
                  <Clock size={16} /> Menu is currently CLOSED — {profile.emergencyShutdown ? 'Temporarily shut down by management' : 'Outside operating hours'}
                </div>
              )}
              {searchQuery && filteredCategories.length === 0 && (
                <div className="py-16 text-center">
                  <Search size={32} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm font-semibold text-slate-500">No items match "{searchQuery}"</p>
                  <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                </div>
              )}
              {filteredCategories.map(cat => (
                <div key={cat.id} id={`spd-cat-${cat.id}`} data-section-id={cat.id} className={cn('mb-8', cat._available === false && 'opacity-40')}>
                  <h3 className="text-base font-bold text-indigo-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FolderOpen size={16} className="text-indigo-400" />
                    {nm(cat.nameEN, cat.nameAR)}
                    <span className="text-[10px] text-slate-400 font-normal lowercase">({cat.items.filter(i => !i.hidden).length} items)</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {cat.items.filter(i => !i.hidden && !(i.campaignMeta && isPromoExpired(i))).map(item => (
                      <div key={item.id} onClick={() => openDrawer(item)}
                        className={cn('bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group flex flex-col', (item as any)._available === false && 'opacity-40')}>
                        <div className="h-36 relative bg-slate-50 shrink-0">
                          {item.image ? <img loading="lazy" src={pubImg(item.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package size={28} className="text-slate-200" /></div>}
                          <button className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"><Plus size={16} /></button>
                        </div>
                        <div className="p-3 flex flex-col flex-1">
                          <div>
                          <p className="text-sm font-bold text-slate-900 truncate">{nm(item.nameEN, item.nameAR)}</p>
                          {(isAR ? item.descriptionAR : item.descriptionEN) && <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{nm(item.descriptionEN || '', item.descriptionAR || '')}</p>}
                          </div>
                          <div className="flex items-center gap-2 mt-auto pt-2">
                            {item.campaignMeta && !isPromoExpired(item) ? (
                              <>
                                <span className="text-[10px] text-slate-400 line-through">{currencySymbol} {item.campaignMeta.originalPrice.toFixed(2)}</span>
                                <span className="text-sm font-bold text-emerald-600">{currencySymbol} {item.campaignMeta.promoPrice.toFixed(2)}</span>
                                <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">{discountLabel(item, currencySymbol)}</span>
                              </>
                            ) : (() => {
const fp = formatCardPrice(item, currencySymbol, pricingStrategy);
                          return fp.text
                            ? <>{fp.prefix && <span className="text-[9px] font-normal text-slate-400 mr-1">{fp.prefix}</span>}<span className="text-xs font-bold text-indigo-600">{fp.text}</span></>
                                : fp.badge ? <span className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-bold">{fp.badge}</span> : null;
                            })()}
                            {!shouldHidePrice(item) && (item.optionGroups || []).length > 0 && <span className="text-[9px] text-slate-400">Customizable</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              </div>
            </div>

            {/* Cart sidebar */}
            {cartCount > 0 && (
              <div className="w-80 shrink-0 border-l border-slate-200 bg-slate-50 flex flex-col">
                <div className="px-5 py-4 border-b border-slate-200 bg-white flex items-center justify-between">
                  <h4 className="text-sm font-bold flex items-center gap-2"><ShoppingCart size={16} className="text-indigo-600" /> Cart ({cartCount})</h4>
                  <button onClick={() => setCart([])} className="text-[10px] text-red-500 font-bold hover:underline">Clear</button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {cart.map(c => {
                    const optP = c.opts.reduce((a, o) => a + o.opt.extraPrice, 0);
                    const lineT = (c.item.basePrice + optP) * c.qty;
                    return (
                      <div key={c.id} className="bg-white rounded-xl p-3 border border-slate-100 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate">{nm(c.item.nameEN, c.item.nameAR)}</p>
                            {c.opts.length > 0 && <p className="text-[10px] text-slate-400 truncate">{c.opts.map(o => nm(o.opt.nameEN, o.opt.nameAR)).join(', ')}</p>}
                          </div>
                          <span className="text-sm font-bold text-indigo-600 whitespace-nowrap">{currencySymbol} {lineT.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Minus size={12} /></button>
                          <span className="text-sm font-bold w-7 text-center">{c.qty}</span>
                          <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Plus size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-4 pb-4 pt-3 border-t border-slate-200 bg-white space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-bold text-slate-800">Total</span><span className="text-lg font-bold text-indigo-600">{currencySymbol} {cartTotal.toFixed(2)}</span></div>
                  <button className="w-full py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-lg">Checkout (Demo)</button>
                </div>
              </div>
            )}
          </div>

        {/* Desktop modifier modal — Pizza Hut side-by-side */}
        {drawer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDrawer(null)}>
            <div className="relative bg-white rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '85vh' }} onClick={e => e.stopPropagation()}>
              {/* Close button — top-right of card */}
              <button onClick={() => setDrawer(null)} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/90 shadow-md hover:bg-white text-slate-600 hover:text-slate-900 flex items-center justify-center transition-all"><X size={18} /></button>
              {/* Side-by-side body — fills entire card */}
              <div className="flex flex-1 min-h-0">
                {/* Left: Image — locked to card height */}
                {drawer.image && (
                  <div className="shrink-0 w-1/2 relative bg-gradient-to-br from-slate-100 to-slate-50">
                    <img loading="lazy" src={pubImg(drawer.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
                {/* Middle: Content + footer — scrolls within card */}
                <div className={cn('flex flex-col min-h-0', drawer.image ? 'w-1/2' : 'w-full')}>
                  <div className="px-5 pt-5 pb-3 shrink-0">
                    <h3 className="text-xl font-bold text-slate-900 pr-8">{nm(drawer.nameEN, drawer.nameAR)}</h3>
                    {(isAR ? drawer.descriptionAR : drawer.descriptionEN) && (
                      <p className="text-sm text-slate-500 mt-1">{nm(drawer.descriptionEN || '', drawer.descriptionAR || '')}</p>
                    )}
                    {drawer.campaignMeta && !isPromoExpired(drawer) ? (
                      <p className="text-sm font-bold mt-2"><span className="text-slate-400 line-through">{currencySymbol} {drawer.campaignMeta.originalPrice.toFixed(2)}</span> <span className="text-emerald-600">{currencySymbol} {drawer.campaignMeta.promoPrice.toFixed(2)}</span></p>
                    ) : !shouldHidePrice(drawer) ? (
                      <p className="text-lg font-bold text-indigo-600 mt-2">{currencySymbol} {drawer.basePrice.toFixed(2)}</p>
                    ) : (
                      (() => { const fp = formatCardPrice(drawer, currencySymbol, pricingStrategy); return fp.text ? <p className="text-lg font-bold text-indigo-600 mt-2">{fp.prefix && <span className="text-sm font-normal text-slate-400 mr-1">{fp.prefix}</span>}{fp.text}</p> : fp.badge ? <p className="text-sm text-slate-400 mt-2">{fp.badge}</p> : null; })()
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5">
                    {/* Divider between description and options */}
                    {(drawer.optionGroups || []).filter(g => !g.hidden).length > 0 && <div className="border-t border-slate-100" />}
                    {(drawer.optionGroups || []).filter(g => !g.hidden).map(grp => {
                      const sel = drawerSelections[grp.id] || new Set();
                      const qMap = drawerQty[grp.id] || {};
                      const min = grp.minSelection ?? 0;
                      const max = grp.maxSelection ?? 0;
                      const isQtyMode = grp.allowMultipleQty;
                      const qtyTotal = isQtyMode ? Object.values(qMap).reduce((a, v) => a + v, 0) : sel.size;
                      const selCount = isQtyMode ? qtyTotal : sel.size;
                      const metMin = selCount >= min;
                      const isReq = min > 0;
                      return (
                        <div key={grp.id}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-sm font-bold text-slate-800">{nm(grp.nameEN, grp.nameAR)}</p>
                              <p className="text-[11px] text-slate-400">{min > 0 && `Min ${min}`}{min > 0 && max > 0 && ' · '}{max > 0 && `Max ${max}`}{!min && !max && 'Optional'}</p>
                            </div>
                            {isReq && !metMin && <div className="w-5 h-5 rounded-full border-2 border-red-300 flex items-center justify-center"><span className="text-[9px] text-red-400">!</span></div>}
                            {metMin && isReq && <div className="w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center"><Check size={11} className="text-white" /></div>}
                          </div>
                          <div className="space-y-1.5">
                            {grp.options.filter(o => !o.hidden).map(opt => {
                              if (isQtyMode) {
                                const qty = qMap[opt.id] || 0;
                                const totalQ = Object.values(qMap).reduce((a, v) => a + v, 0);
                                const atMax = max > 0 && totalQ >= max;
                                return (
                                  <div key={opt.id} className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all',
                                    qty > 0 ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200')}>
                                    <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                                    <div className="flex items-center gap-2">
                                      {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{currencySymbol} {opt.extraPrice.toFixed(2)}{qty > 1 ? ` × ${qty}` : ''}</span>}
                                      <div className="flex items-center gap-1">
                                        <button onClick={() => changeOptQty(grp.id, opt.id, -1, max)} disabled={qty === 0}
                                          className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                            qty === 0 ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Minus size={12} /></button>
                                        <span className="w-5 text-center text-xs font-bold text-slate-700">{qty}</span>
                                        <button onClick={() => changeOptQty(grp.id, opt.id, 1, max)} disabled={atMax}
                                          className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-colors',
                                            atMax ? 'border-slate-200 text-slate-300' : 'border-indigo-300 text-indigo-600 hover:bg-indigo-100')}><Plus size={12} /></button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              const selected = sel.has(opt.id);
                              const disabled = !selected && max > 0 && selCount >= max && max !== 1;
                              return (
                                <button key={opt.id} onClick={() => !disabled && toggleOpt(grp.id, opt.id, max)} disabled={disabled}
                                  className={cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                                    selected ? 'border-indigo-400 bg-indigo-50' : disabled ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 hover:border-indigo-300')}>
                                  <span className="text-sm font-medium text-slate-800">{nm(opt.nameEN, opt.nameAR)}</span>
                                  <div className="flex items-center gap-2">
                                    {opt.extraPrice > 0 && <span className="text-xs text-indigo-600 font-bold">+{currencySymbol} {opt.extraPrice.toFixed(2)}</span>}
                                    <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', selected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300')}>{selected && <Check size={12} className="text-white" />}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {/* Special Request */}
                    {drawer.allowSpecialRequests !== false && (
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <p className="text-sm font-bold text-slate-700">Special Request</p>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                        </div>
                        <div className="relative">
                          <textarea
                            value={drawerSpecialReq}
                            onChange={e => setDrawerSpecialReq(e.target.value.slice(0, 200))}
                            placeholder="Leave any instructions to the restaurant"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                            rows={3}
                          />
                          <span className="absolute bottom-2 right-3 text-[9px] text-slate-300">{drawerSpecialReq.length}/200</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Footer: qty stepper + add to cart — inside content side only */}
                  <div className="px-5 py-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-2 py-1.5">
                        <button onClick={() => setDrawerItemQty(q => Math.max(1, q - 1))} disabled={drawerItemQty <= 1}
                          className={cn('w-8 h-8 rounded-lg flex items-center justify-center transition-colors', drawerItemQty <= 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100')}><Minus size={16} /></button>
                        <span className="w-8 text-center text-base font-bold text-slate-800">{drawerItemQty}</span>
                        <button onClick={() => setDrawerItemQty(q => q + 1)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"><Plus size={16} /></button>
                      </div>
                      <button onClick={addToCart} disabled={!drawerValid}
                        className={cn('flex-1 py-3 rounded-2xl text-sm font-bold transition-all flex items-center justify-center gap-2',
                          drawerValid ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg' : 'bg-slate-200 text-slate-400 cursor-not-allowed')}>
                        {drawerValid ? (
                          <>
                            <span>{shouldHidePrice(drawer) ? `${currencySymbol} ${(drawerExtraPrice * drawerItemQty).toFixed(2)}` : `${currencySymbol} ${((drawer.basePrice + drawerExtraPrice) * drawerItemQty).toFixed(2)}`}</span>
                            <span>Add to Cart</span>
                          </>
                        ) : 'Select required options'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Desktop item detail modal — Pizza Hut side-by-side */}
        {detailItem && !drawer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDetailItem(null)}>
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()}>
              {/* Close button */}
              <button onClick={() => setDetailItem(null)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 text-slate-600 flex items-center justify-center transition-colors"><X size={16} /></button>
              {/* Side-by-side body */}
              <div className="flex flex-col md:flex-row flex-1 min-h-0">
                {/* Left: Image */}
                {detailItem.image ? (
                  <div className="w-full md:w-1/2 shrink-0">
                    <div className="aspect-square w-full relative bg-gradient-to-br from-slate-100 to-slate-50">
                      <img loading="lazy" src={pubImg(detailItem.image)} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full md:w-1/2 shrink-0">
                    <div className="aspect-square w-full bg-gradient-to-br from-indigo-50 to-slate-100 flex items-center justify-center">
                      <Package size={64} className="text-indigo-200" />
                    </div>
                  </div>
                )}
                {/* Right: Content */}
                <div className={cn('flex flex-col flex-1 min-h-0', detailItem.image ? 'md:w-1/2' : 'md:w-1/2')}>
                  <div className="flex-1 overflow-y-auto px-6 pt-5 pb-4" style={{ maxHeight: '55vh' }}>
                    <h3 className="text-xl font-bold text-slate-900 pr-8">{nm(detailItem.nameEN, detailItem.nameAR)}</h3>
                    {(isAR ? detailItem.descriptionAR : detailItem.descriptionEN) && (
                      <p className="text-sm text-slate-500 mt-2 leading-relaxed whitespace-pre-wrap">{nm(detailItem.descriptionEN || '', detailItem.descriptionAR || '')}</p>
                    )}
                    {renderDetailPrice(detailItem)}
                    {/* Divider */}
                    <div className="border-t border-slate-100 my-4" />
                    {/* Special Request — decoupled from option groups */}
                    {detailItem.allowSpecialRequests !== false && (
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <p className="text-sm font-bold text-slate-700">Special Request</p>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-medium">Optional</span>
                        </div>
                        <div className="relative">
                          <textarea
                            value={detailSpecialReq}
                            onChange={e => setDetailSpecialReq(e.target.value.slice(0, 200))}
                            placeholder="Leave any instructions to the restaurant"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                            rows={3}
                          />
                          <span className="absolute bottom-2 right-3 text-[9px] text-slate-300">{detailSpecialReq.length}/200</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Sticky footer */}
              <div className="px-5 py-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
                <button onClick={addFromDetail}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg transition-all">
                  {detailCartPrice(detailItem) ? `${currencySymbol} ${detailCartPrice(detailItem)}  Add to Cart` : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Footer branding */}
      <div className="text-center py-4">
        <p className="text-[10px] text-slate-400">Powered by ClickIt Engine</p>
      </div>

      {/* Success Toast */}
      {toastMessage && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[100] pointer-events-none"
          style={{ animation: 'fadeInUp 0.3s ease-out' }}>
          <div className="bg-slate-900 text-white text-sm font-bold px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2">
            <Check size={16} className="text-emerald-400 shrink-0" />
            {toastMessage}
          </div>
        </div>
      )}

      {/* Floating Share Button */}
      {menuData && (
        <button
          onClick={async () => {
            const url = window.location.href;
            if (navigator.share) {
              try { await navigator.share({ title: menuData.name, url }); } catch {}
            } else {
              await navigator.clipboard.writeText(url);
              setShareCopied(true);
              setTimeout(() => setShareCopied(false), 2000);
            }
          }}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 bg-indigo-600 text-white rounded-full shadow-xl flex items-center justify-center hover:bg-indigo-700 transition-all hover:scale-105"
          title="Share this menu">
          <Share2 size={20} />
          {shareCopied && (
            <span className="absolute -top-10 right-0 bg-slate-800 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
              Link copied!
            </span>
          )}
        </button>
      )}
    </div>
  );
}



